<script lang="ts">
  import { ResponsiveVars } from '@twist/core';
  import { createEventDispatcher, type Snippet } from 'svelte';
  import { fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { clickOutside } from 'svelte-use-click-outside';
  import sortBy from 'lodash/sortBy';

  import ChevronDown from '$components/Icons/ChevronDown.svelte';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import SelectOption from './SelectOption.svelte';
  import type { Option } from './types';
  import { browser } from '$app/environment';
  import { flagValue } from '$lib/context/flags';
  import { FEATURE_FLAGS } from '$lib/flags/constants';
  import FontSizeDecorator from '$components/SanityComponents/PortableText/Decorators/FontSizeDecorator.svelte';

  interface Props {
    label?: string;
    options?: Option[];
    selected?: string[];
    children?: Snippet;
  }

  let { label = 'Select', options = [], selected = $bindable([]), children }: Props = $props();

  let open = $state(false);
  let hasFocusOrHover = $state(false);
  let buttonRef: HTMLButtonElement | undefined = $state();

  const dispatch = createEventDispatcher();

  const isTouchDevice = browser && matchMedia('(hover: none) and (pointer: coarse)').matches;

  const toggleMenu = () => {
    if (open && isTouchDevice) {
      hasFocusOrHover = false;
    } else {
      hasFocusOrHover = true;
    }

    open = !open;
  };

  const handleOptionKeyDown = (event: KeyboardEvent) => {
    switch (event.code) {
      case 'Escape':
        open = false;
        buttonRef?.focus();
        break;
      case 'Space':
        event.preventDefault();
        handleOption(event);
        break;
      case 'ArrowUp':
        event.preventDefault();

        if (document.activeElement?.previousElementSibling) {
          const previousSibling = document.activeElement?.previousElementSibling as HTMLLIElement;
          previousSibling.focus();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();

        if (document.activeElement?.nextElementSibling) {
          const nextSibling = document.activeElement?.nextElementSibling as HTMLLIElement;
          nextSibling.focus();
        }
        break;
      default:
        return;
    }
  };

  const handleOption = (event: Event) => {
    const value = (event.currentTarget as Element)?.getAttribute('data-value');

    if (value && selected.includes(value)) {
      selected = selected.filter((v) => v !== value);
    } else if (value) {
      selected = [...selected, value];
    }

    dispatch('change', selected);
  };

  let sortedOptions = $derived(sortBy(options, (option) => option.label));

  let brandReskinEnabled = $derived(flagValue(FEATURE_FLAGS.BRAND_RESKIN));

  const responsiveVars = new ResponsiveVars()
    .addFluid('select-height', toFluidVarInput(45, 49))
    .addFluid('select-font-size', toFluidVarInput(18, 18))
    .addFluid('select-gap', toFluidVarInput(9, 9))
    .addFluid('select-px', toFluidVarInput(15, 15))
    .addFluid('select-py', toFluidVarInput(12, 12))
    .addFluid('select-count-min-width', toFluidVarInput(38, 38))
    .addFluid('select-min-width', toFluidVarInput(146, 146))
    .addFluid('icon-size', toFluidVarInput(16, 16))
    .addFluid('options-max-height', toFluidVarInput(400, 400))
    .addFluid('options-width', toFluidVarInput(310, 310))
    .addFluid('options-pt', toFluidVarInput(3, 3))
    .addFluid('options-pb', toFluidVarInput(6, 6))
    .toCSS();
</script>

<div use:clickOutside={() => (open = false)} class={responsiveVars}>
  <button
    bind:this={buttonRef}
    name="select-menu"
    class="select-menu"
    class:focus-hover={hasFocusOrHover}
    id="select-menu"
    onclick={toggleMenu}
    onmouseenter={() => (hasFocusOrHover = true)}
    onfocus={() => (hasFocusOrHover = true)}
    onmouseleave={() => (hasFocusOrHover = false)}
    onblur={() => (hasFocusOrHover = false)}
    type="button"
    aria-haspopup="listbox"
    aria-label={`${selected.length} options for ${label} selected`}
    aria-expanded={open}
    disabled={options.length === 0}
  >
    <span class="select-menu--left">
      {#if brandReskinEnabled}
        <FontSizeDecorator size="normal">
          <span class="select-menu__label">{label}</span>
        </FontSizeDecorator>
        <FontSizeDecorator size="breadcrumb">
          <span class="select-menu__count">{selected.length ? `[ ${selected.length} ]` : ''}</span>
        </FontSizeDecorator>
      {:else}
        <span class="select-menu__label">{label}</span>
        <span class="select-menu__count">{selected.length ? `[ ${selected.length} ]` : ''}</span>
      {/if}
    </span>
    <span class="select-menu--right">
      <span class="select-menu__icon" class:select-menu__icon--open={open}>
        <ChevronDown />
      </span>
      {@render children?.()}
    </span>
  </button>
  {#if open}
    <div class="select-options-container">
      <ul
        transition:fly={{ duration: 150, x: 0, y: -9, opacity: 0, easing: cubicOut }}
        class="select-options"
        role="listbox"
        tabIndex={-1}
        aria-labelledby="select-menu"
        aria-multiselectable="true"
      >
        {#each sortedOptions as option}
          <SelectOption
            {option}
            isSelected={selected.includes(option.value)}
            {handleOption}
            {handleOptionKeyDown}
          />
        {/each}
      </ul>
    </div>
  {/if}
</div>

<style lang="scss">
  @import 'src/scss/media';

  .select-menu {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--select-gap); // var(--twist-core-spacing-3);
    border: 1px solid var(--twist-color-gray-100);
    border-radius: 1000px; // replace with var(--twist-core-border-radius-round);
    height: var(--select-height);
    min-width: var(--select-min-width);
    width: 100%;
    padding: var(--select-py) var(--select-px); // var(--twist-core-spacing-4) var(--twist-core-spacing-5);
    font-family: var(--font-family-primary);
    font-size: var(--select-font-size);
    font-weight: 600;
    line-height: 140%;
    background-color: var(--twist-color-gray-0);
    color: var(--twist-color-gray-100);
    cursor: pointer;
    outline: none;

    &.focus-hover:not(:has(:focus)) {
      border-color: var(--twist-color-gray-0);
      background-color: var(--twist-color-gray-100);
      color: var(--twist-color-gray-0);
    }

    &:active:not(:has(.focus-hover, :active)) {
      opacity: 50%;
    }

    &:disabled {
      opacity: 50%;
      cursor: not-allowed;
    }
  }

  .select-menu--left {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--select-gap); // var(--twist-core-spacing-3);
  }

  .select-menu--right {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: var(--select-gap); // var(--twist-core-spacing-3);
  }

  .select-menu__icon {
    display: flex;
    align-items: center;
    height: var(--icon-size);
    width: var(--icon-size);
    transition: transform var(--twist-ease-out-1) var(--twist-duration-150);

    &--open {
      transform: rotate(180deg);
    }
  }

  .select-menu__count {
    min-width: var(--select-count-min-width);
    align-self: flex-start;

    @include at('lg') {
      align-self: center;
    }
  }

  .select-options-container {
    position: absolute;
    width: 100%;
    padding-right: 48px;
    z-index: map-get($zIndex, select-options);

    @include at('lg') {
      max-width: var(--options-width);
    }
  }

  .select-options {
    max-height: var(--options-max-height);
    border-radius: 9px; // replace with var(--twist-core-border-radius-3)
    border: 1px solid var(--twist-color-gray-100);
    padding: var(--options-pt) 0 var(--options-pb) 0;
    background-color: var(--twist-color-gray-0);
    color: var(--twist-color-gray-100);
    overflow-y: scroll;

    &::-webkit-scrollbar {
      display: none;
    }
  }
</style>
